import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import {
  Clock, CheckCircle2, XCircle, ShieldAlert, ClipboardCheck, Hammer,
  FileCheck2, ClipboardList, ArrowLeftRight, Wrench, UserPlus, Briefcase,
  Activity, Sparkles, AlertTriangle,
} from "lucide-react";

const TYPE_META = {
  "timesheet.submitted":  { icon: Clock, color: "text-yellow-600", verb: "logged hours" },
  "timesheet.approved":   { icon: CheckCircle2, color: "text-green-600", verb: "approved a timesheet" },
  "timesheet.rejected":   { icon: XCircle, color: "text-red-600", verb: "rejected a timesheet" },
  "prestart.submitted":   { icon: Wrench, color: "text-blue-600", verb: "did a pre-start" },
  "toolbox.created":      { icon: ClipboardCheck, color: "text-purple-600", verb: "posted a toolbox" },
  "toolbox.signed":       { icon: ClipboardCheck, color: "text-green-600", verb: "signed a toolbox" },
  "sssp.created":         { icon: FileCheck2, color: "text-blue-600", verb: "started an SSSP" },
  "sssp.locked":          { icon: FileCheck2, color: "text-green-600", verb: "locked an SSSP" },
  "form.submitted":       { icon: ClipboardList, color: "text-[#FF5F15]", verb: "submitted a form" },
  "gear.checkout":        { icon: ArrowLeftRight, color: "text-[#FF5F15]", verb: "took gear" },
  "gear.return":          { icon: CheckCircle2, color: "text-green-600", verb: "returned gear" },
  "gear.return_broken":   { icon: AlertTriangle, color: "text-red-600", verb: "returned gear broken" },
  "gear.repaired":        { icon: Wrench, color: "text-green-600", verb: "marked gear repaired" },
  "user.joined":          { icon: UserPlus, color: "text-blue-600", verb: "joined the crew" },
  "job.created":          { icon: Briefcase, color: "text-[#FF5F15]", verb: "created a job" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function LiveActivityFeed() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sinceRef = useRef(null);
  const pulseRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  const load = async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : { limit: 25 };
      const { data } = await api.get("/activity", { params });
      if (data.length > 0) {
        setEvents((prev) => {
          // Merge new on top, keep most recent 50
          const merged = [...data, ...prev];
          const seen = new Set();
          const deduped = merged.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
          return deduped.slice(0, 50);
        });
        sinceRef.current = data[0].created_at;
        // Visual pulse when new events arrive after initial load
        if (loaded) {
          setPulse(true);
          if (pulseRef.current) clearTimeout(pulseRef.current);
          pulseRef.current = setTimeout(() => setPulse(false), 1200);
        }
      } else if (!sinceRef.current && events.length === 0) {
        // Initial load returned nothing
      }
      // For initial GET we get a list — set since to the newest one
      if (!sinceRef.current && data[0]) sinceRef.current = data[0].created_at;
    } catch (e) {
      // swallow — non-blocking widget
    } finally {
      setLoaded(true);
    }
  };

  usePoll(load, 15000);
  useEffect(() => () => { if (pulseRef.current) clearTimeout(pulseRef.current); }, []);

  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
  return (
    <div
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#101620] overflow-hidden"
      data-testid="live-activity-feed"
    >
      <div
        className={`flex items-center justify-between px-5 py-3 border-b border-zinc-200 dark:border-zinc-800 transition-colors ${
          pulse ? "bg-orange-50 dark:bg-orange-950/20" : "bg-white dark:bg-[#101923]"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2"><Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" /></span>
          <span className="text-[15px] font-bold text-slate-900 dark:text-slate-100" style={{ ...fontHead, fontWeight: 700 }}>
            Activity feed
          </span>
          <span className="relative flex h-1.5 w-1.5 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5F15] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF5F15]"></span>
          </span>
        </div>
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500" style={{ ...fontHead, fontWeight: 700 }}>
          Live updates
        </span>
      </div>

      <div className="max-h-72 overflow-y-auto" data-testid="activity-feed-list">
        {!loaded ? (
          <div className="px-5 py-6 text-[11px] uppercase tracking-[0.18em] text-zinc-500" style={{ ...fontHead, fontWeight: 700 }}>Loading…</div>
        ) : events.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <Sparkles className="w-5 h-5 text-zinc-300 dark:text-zinc-700 mx-auto mb-2" />
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500" style={{ ...fontHead, fontWeight: 700 }}>
              Quiet on the wire.
            </p>
            <p className="text-[9px] uppercase tracking-[0.16em] text-zinc-400 mt-1" style={{ ...fontHead, fontWeight: 700 }}>
              Activity will show up here as your crew works.
            </p>
          </div>
        ) : (
          <ul>
            {events.map((e) => (
              <ActivityRow key={e.id} event={e} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ActivityRow({ event }) {
  const meta = TYPE_META[event.type] || { icon: Activity, color: "text-zinc-500", verb: "did something" };
  const Icon = meta.icon;
  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
  // pull a tailwind text-color into a CSS color for the icon-border
  const tint = meta.color.includes("green") ? "bg-emerald-50 dark:bg-emerald-950/30" :
    meta.color.includes("red") ? "bg-red-50 dark:bg-red-950/30" :
    meta.color.includes("blue") ? "bg-blue-50 dark:bg-blue-950/30" :
    meta.color.includes("purple") ? "bg-purple-50 dark:bg-purple-950/30" : "bg-orange-50 dark:bg-orange-950/30";
  return (
    <li
      className="px-4 py-3 flex items-center gap-3 border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-[#151C27] transition-colors"
      data-testid={`activity-row-${event.id}`}
    >
      <div
        className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${tint} ${meta.color}`}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] leading-tight">
          <span
            className="uppercase tracking-[0.04em] text-zinc-900 dark:text-white"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            {event.actor_name || "Someone"}
          </span>{" "}
          <span className="text-zinc-600 dark:text-zinc-400">{event.summary}</span>
        </div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1" style={{ ...fontHead, fontWeight: 700 }}>
          {event.type.replaceAll(".", " · ").replaceAll("_", " ")}
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] tabular-nums text-zinc-400 dark:text-zinc-500 shrink-0" style={{ ...fontHead, fontWeight: 700 }}>
        {timeAgo(event.created_at)}
      </div>
    </li>
  );
}
